import React, { useState, useEffect } from "react";
import Banner2 from "../../components/Banner2";
import { Col, Container, Form, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { createProductInquiry } from "../../features/productInquiry";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SelectDropdown from "../../components/SelectDropdown";
import { getCountries, getCountryCallingCode } from "libphonenumber-js";
import MobileNumberInput from "../../components/MobileNumberInput";

const CreditLife = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  const handleInputChange = (name, value) => {
    setFormData({ ...formData, [name]: value });

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  /* ---------------- VALIDATION ONLY ---------------- */

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.title) newErrors.title = t("validation.title_required");
    if (!formData.name) newErrors.name = t("validation.name_required");
    if (!formData.date_of_birth) newErrors.date_of_birth = t("validation.dob_required");
    if (!formData.insured_amount) newErrors.insured_amount = t("validation.invalid_amount");
    if (!formData.period_of_loan) newErrors.period_of_loan = t("validation.selection_required");
    if (!formData.type_of_loan) newErrors.type_of_loan = t("validation.selection_required");

    if (!formData.email) {
      newErrors.email = t("validation.email_required");
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = t("validation.email_invalid");
    }

    if (!formData.mobile_number) {
      newErrors.mobile_number = t("validation.phone_required");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const getMaxDOB = () => {
    const today = new Date();
    return today.toISOString().split("T")[0]; // yyyy-mm-dd format
  };

  const maxDOB = getMaxDOB();

  /* ---------------- SUBMIT ---------------- */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(t("validation.please_fix_errors"));
      return;
    }

    try {
      const response = await dispatch(
        createProductInquiry({
          form_data: formData,
          product_details: JSON.parse(localStorage.getItem("product_details")),
        })
      ).unwrap();

      if (response.success) {
        toast.success(t("success_res"));
        navigate("/");
      }
    } catch (error) {
      toast.error(t("error_res"));
    }
  };

  return (
    <Banner2 classList="bg-grey">
      <Container>
        <Row>
          <Col className="offset-md-3" md={9}>
            <div className="insurance-title">
              <h3 className="title text-capitalize text-center">
                {t("credit_life.description")}
              </h3>
            </div>
          </Col>

          <Col md={3}>
            <div className="policy-box">
              <div className="icon-image">
                <img
                  src={require("../../assets/SVG/1-24.svg").default}
                  alt="icon"
                  className="img-fluid"
                />
              </div>
              <p className="title">{t("credit_life.title")}</p>
            </div>
          </Col>

          <Col md={9}>
            <div className="policy-form">
              <div className="policy-formBox why-us-form">
                <Form onSubmit={handleSubmit}>
                  <Row>

                    <Col md={6} lg={4}>
                      <div className="form-group">
                        <SelectDropdown
                          name="title"
                          placeholder={t("credit_life.form.title.placeholder")}
                          selectedOption={formData.title ?? ""}
                          options={[
                            { value: "mr", label: t("credit_life.form.title.option1") },
                            { value: "mrs", label: t("credit_life.form.title.option2") },
                            { value: "miss", label: t("credit_life.form.title.option3") },
                          ]}
                          handleOnChange={handleInputChange}
                        />
                        {errors.title && <small className="text-danger">{errors.title}</small>}
                      </div>
                    </Col>

                    <Col md={6} lg={8}>
                      <div className="form-group">
                        <Form.Control
                          type="text"
                          isInvalid={!!errors.name}
                          placeholder={t("credit_life.form.name")}
                          className="form-control element-style"
                          onChange={(e) =>
                            handleInputChange("name", e.target.value)
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.name}
                        </Form.Control.Feedback>
                      </div>
                    </Col>

                    <Col lg={12} md={12}>
                      <div className="form-group">
                        <Form.Label>{t("credit_life.form.dob")}</Form.Label>
                        <Form.Control
                          type="date"
                          max={maxDOB}
                          isInvalid={!!errors.date_of_birth}
                          className="form-control element-style"
                          value={formData?.date_of_birth || ""}
                          onChange={(e) =>
                            handleInputChange("date_of_birth", e.target.value)
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.date_of_birth}
                        </Form.Control.Feedback>
                      </div>
                    </Col>

                    <Col md={6}>
                      <div className="form-group">
                        <Form.Control
                          type="number"
                          isInvalid={!!errors.insured_amount}
                          placeholder={t("credit_life.form.insured_amount")}
                          className="form-control element-style"
                          onChange={(e) =>
                            handleInputChange("insured_amount", e.target.value)
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.insured_amount}
                        </Form.Control.Feedback>
                      </div>
                    </Col>

                    <Col md={6}>
                      <div className="form-group">
                        <SelectDropdown
                          name="period_of_loan"
                          placeholder={t("credit_life.form.period_of_loan.placeholder")}
                          selectedOption={formData.period_of_loan ?? ""}
                          options={[
                            { value: "1", label: "1 Year" },
                            { value: "3", label: "3 Years" },
                            { value: "5", label: "5 Years" },
                          ]}
                          handleOnChange={handleInputChange}
                        />
                        {errors.period_of_loan && (
                          <small className="text-danger">{errors.period_of_loan}</small>
                        )}
                      </div>
                    </Col>

                    <Col md={6}>
                      <div className="form-group">
                        <SelectDropdown
                          name="type_of_loan"
                          placeholder={t("credit_life.form.type_of_loan.placeholder")}
                          selectedOption={formData.type_of_loan ?? ""}
                          options={[
                            { value: "personal_loan", label: t("credit_life.form.type_of_loan.option1") },
                            { value: "home_loan", label: t("credit_life.form.type_of_loan.option2") },
                            { value: "vehicle_loan", label: t("credit_life.form.type_of_loan.option3") },
                          ]}
                          handleOnChange={handleInputChange}
                        />
                        {errors.type_of_loan && (
                          <small className="text-danger">{errors.type_of_loan}</small>
                        )}
                      </div>
                    </Col>

                    <Col md={6}>
                      <div className="form-group">
                        <Form.Control
                          type="email"
                          isInvalid={!!errors.email}
                          placeholder={t("credit_life.form.email")}
                          className="form-control element-style"
                          onChange={(e) =>
                            handleInputChange("email", e.target.value)
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.email}
                        </Form.Control.Feedback>
                      </div>
                    </Col>

                    <Col>
                      <MobileNumberInput
                        onChange={({ countryCode, number }) => {
                          handleInputChange("country_code", countryCode);
                          handleInputChange("mobile_number", number);
                        }}
                      />
                      {errors.mobile_number && (
                        <small className="text-danger">{errors.mobile_number}</small>
                      )}
                    </Col>

                    <Col md={12}>
                      <div className="form-submitbtn mt-2">
                        <button type="submit" className="btn h-btn">
                          {t("credit_life.form.confirm")}
                        </button>
                      </div>
                    </Col>

                  </Row>
                </Form>
              </div>
            </div>
          </Col>

        </Row>
      </Container>
    </Banner2>
  );
};

export default CreditLife;